import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, Upload, Loader, Eye, EyeOff, Image as ImageIcon } from 'lucide-react';
import MediaSelector from '@/components/admin/MediaSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'familia', label: 'Viajes de Familia' },
  { value: 'novios', label: 'Viajes de Novios' },
  { value: 'grupos', label: 'Viajes de Grupos' },
  { value: 'cruceros', label: 'Cruceros' },
  { value: 'bienestar', label: 'Bienestar y Relax' },
  { value: 'arte', label: 'Arte e Historia' },
  { value: 'lujo', label: 'Lujo y Compras' },
  { value: 'deportivo', label: 'Deportivo y Cultural' },
  { value: 'enologico', label: 'Enológicos y Gastronómicos' },
  { value: 'naturaleza', label: 'Naturaleza y Aventura' },
  { value: 'medida', label: 'Viajes a Medida' },
];

const EMPTY_TRIP = {
  title: '',
  description: '',
  short_description: '',
  categories: [], // Ahora es lista
  destination: '',
  price: '',
  duration: '',
  image_url: '',
  includes: '',
  highlights: '',
  is_featured: false,
  is_active: true,
  discount: '',
  advisor_name: '',
  advisor_bio: '',
  advisor_image: '',
  show_advisor: false
};

export default function AdminTrips() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [formData, setFormData] = useState(EMPTY_TRIP);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // 'trip' or 'advisor'
  const [showMediaSelector, setShowMediaSelector] = useState(null); // 'trip' or 'advisor'
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    active: 'all',
    featured: 'all',
    minPrice: '',
    maxPrice: ''
  });

  const fileInputRef = useRef(null);
  const advisorFileInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTrips = async () => {
    try {
      let url = `${API}/trips?all_trips=true`;
      if (filters.search) url += `&search=${filters.search}`;
      if (filters.category !== 'all') url += `&category=${filters.category}`;
      if (filters.active !== 'all') url += `&active=${filters.active === 'true'}`;
      if (filters.featured !== 'all') url += `&featured=${filters.featured === 'true'}`;
      if (filters.minPrice) url += `&min_price=${filters.minPrice}`;
      if (filters.maxPrice) url += `&max_price=${filters.maxPrice}`;
      
      const res = await axios.get(url, { headers });
      setTrips(res.data);
    } catch (err) {
      console.error('Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTrips();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const openCreateDialog = () => {
    setEditingTrip(null);
    setFormData(EMPTY_TRIP);
    setDialogOpen(true);
  };

  const openEditDialog = (trip) => {
    setEditingTrip(trip);
    // Asegurar que categories es un array y manejar retrocompatibilidad
    const categoriesList = trip.categories || (trip.category ? [trip.category] : []);
    
    setFormData({
      ...EMPTY_TRIP,
      ...trip,
      categories: categoriesList,
      discount: trip.discount || '',
      price: trip.price.toString(),
      includes: trip.includes?.join('\n') || '',
      highlights: trip.highlights?.join('\n') || ''
    });
    setDialogOpen(true);
  };

  const handleCategoryToggle = (catValue) => {
    setFormData(prev => {
      const current = prev.categories || [];
      if (current.includes(catValue)) {
        return { ...prev, categories: current.filter(c => c !== catValue) };
      } else {
        return { ...prev, categories: [...current, catValue] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = {
      ...formData,
      price: parseFloat(formData.price),
      includes: formData.includes.split('\n').filter(Boolean),
      highlights: formData.highlights.split('\n').filter(Boolean),
      // Mantenemos plural y singular para evitar problemas en modelos Pydantic
      category: formData.categories[0] || '',
      categories: formData.categories
    };

    try {
      if (editingTrip) {
        await axios.put(`${API}/trips/${editingTrip.id}`, data, { headers });
        toast.success('Viaje actualizado');
      } else {
        await axios.post(`${API}/trips`, data, { headers });
        toast.success('Viaje creado');
      }
      setDialogOpen(false);
      fetchTrips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tripId) => {
    if (!window.confirm('¿Estás seguro de eliminar este viaje?')) return;

    try {
      await axios.delete(`${API}/trips/${tripId}`, { headers });
      toast.success('Viaje eliminado');
      fetchTrips();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const toggleTripStatus = async (trip) => {
    try {
      await axios.put(`${API}/trips/${trip.id}`, { is_active: !trip.is_active }, { headers });
      toast.success(`Viaje ${!trip.is_active ? 'activado' : 'desactivado'}`);
      fetchTrips();
    } catch (err) {
      toast.error('Error al cambiar el estado');
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div className="flex-1">
          <h1 className="text-3xl font-serif font-semibold text-primary" data-testid="admin-trips-title">
            Gestión de Viajes
          </h1>
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="w-full md:w-64">
              <Input 
                placeholder="Buscar por nombre o destino..." 
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="bg-white"
              />
            </div>
            <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
              <SelectTrigger className="w-40 bg-white">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.active} onValueChange={(v) => setFilters({...filters, active: v})}>
              <SelectTrigger className="w-32 bg-white">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Cualquier estado</SelectItem>
                <SelectItem value="true">Activos</SelectItem>
                <SelectItem value="false">Inactivos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.featured} onValueChange={(v) => setFilters({...filters, featured: v})}>
              <SelectTrigger className="w-32 bg-white">
                <SelectValue placeholder="Destacado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Destacados</SelectItem>
                <SelectItem value="false">No destacados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="btn-accent" data-testid="create-trip-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Viaje
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif">
                {editingTrip ? 'Editar Viaje' : 'Nuevo Viaje'}
              </DialogTitle>
              <DialogDescription>
                {editingTrip 
                  ? `Modifica los detalles del viaje: ${formData.title}` 
                  : 'Rellena los datos para crear un nuevo paquete de viaje.'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="trip-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Título *</label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ej: Maldivas en Familia"
                  />
                </div>
                <div>
                  <label className="form-label">Destino *</label>
                  <Input
                    required
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    placeholder="Ej: Maldivas"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="form-label">Categorías (Selecciona una o varias) *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    {CATEGORIES.map((cat) => (
                      <label key={cat.value} className="flex items-center gap-2 cursor-pointer group">
                        <div 
                          onClick={() => handleCategoryToggle(cat.value)}
                          className={`w-4 h-4 rounded border transition-colors flex items-center justify-center ${
                            formData.categories?.includes(cat.value) 
                              ? 'bg-accent border-accent text-white' 
                              : 'bg-white border-slate-300 group-hover:border-accent'
                          }`}
                        >
                          {formData.categories?.includes(cat.value) && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                        </div>
                        <span className="text-sm text-slate-600 select-none">{cat.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Precio (€) *</label>
                  <Input
                    type="number"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="3500"
                  />
                </div>
                <div>
                  <label className="form-label">Duración *</label>
                  <Input
                    required
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="8 días / 7 noches"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                   <label className="form-label">Descuento (Opcional)</label>
                   <Input
                     value={formData.discount}
                     onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                     placeholder="Ej: -15% o 200€ Dto."
                   />
                </div>
              </div>

              <div>
                <label className="form-label">Descripción corta *</label>
                <Input
                  required
                  value={formData.short_description}
                  onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                  placeholder="Una línea que resuma el viaje"
                />
              </div>

              <div>
                <label className="form-label">Descripción completa *</label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción detallada del viaje"
                  rows={4}
                />
              </div>

              <div>
                <label className="form-label flex justify-between items-center">
                  Imagen del viaje *
                  <span className="text-[10px] text-muted-foreground font-normal">
                    Recomendado: 1200x800 px (3:2)
                  </span>
                </label>
                <div className="flex gap-2">
                  <Input
                    required
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://... o sube una foto"
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    title="Seleccionar de biblioteca"
                    onClick={() => setShowMediaSelector('trip')}
                  >
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleImageUpload(e, 'trip')}
                    className="hidden"
                    accept="image/*"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage === 'trip'}
                    className="shrink-0"
                  >
                    {uploadingImage === 'trip' ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {formData.image_url && (
                  <div className="mt-2 relative group w-full h-32 overflow-hidden rounded-lg border border-slate-200">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">El viaje incluye (uno por línea)</label>
                  <Textarea
                    value={formData.includes}
                    onChange={(e) => setFormData({ ...formData, includes: e.target.value })}
                    placeholder="Vuelos&#10;Hotel 5*&#10;Desayunos"
                    rows={4}
                  />
                </div>
                <div>
                  <label className="form-label">Destacados (uno por línea)</label>
                  <Textarea
                    value={formData.highlights}
                    onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                    placeholder="Snorkel con tortugas&#10;Cena en la playa"
                    rows={4}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6 py-2 border-y border-slate-100">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                    Viaje Activo (Visible en la web)
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
                  />
                  <label htmlFor="is_featured" className="text-sm font-medium text-slate-700">
                    Mostrar en Destacados (Portada)
                  </label>
                </div>
              </div>

              {/* Advisor Section */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-800 flex items-center gap-2">
                    Información del Agente
                  </h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="show_advisor"
                      checked={formData.show_advisor}
                      onChange={(e) => setFormData({ ...formData, show_advisor: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="show_advisor" className="text-xs text-slate-600">
                      Mostrar agente en este viaje
                    </label>
                  </div>
                </div>

                {formData.show_advisor && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nombre del Agente</label>
                        <Input
                          value={formData.advisor_name}
                          onChange={(e) => setFormData({ ...formData, advisor_name: e.target.value })}
                          placeholder="Ej: Laura Sánchez"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Breve Bio</label>
                        <Textarea
                          value={formData.advisor_bio}
                          onChange={(e) => setFormData({ ...formData, advisor_bio: e.target.value })}
                          placeholder="Experta en destinos exóticos..."
                          rows={2}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Foto del Agente</label>
                      <div className="flex gap-2 items-start mt-1">
                        <div className="relative group w-16 h-16 shrink-0 bg-white rounded-full border-2 border-slate-200 overflow-hidden">
                          {formData.advisor_image ? (
                            <img src={formData.advisor_image} alt="Agente" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300 bg-slate-50">
                              <Upload className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input
                            value={formData.advisor_image}
                            onChange={(e) => setFormData({ ...formData, advisor_image: e.target.value })}
                            placeholder="URL foto agente"
                            className="text-xs h-8"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm" 
                              className="h-8 text-[10px] gap-1"
                              onClick={() => setShowMediaSelector('advisor')}
                            >
                              <ImageIcon className="w-3 h-3" /> BIBLIOTECA
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => advisorFileInputRef.current?.click()}
                              disabled={uploadingImage === 'advisor'}
                              className="h-8 text-[10px]"
                            >
                              {uploadingImage === 'advisor' ? <Loader className="w-3 h-3 animate-spin" /> : 'SUBIR FOTO'}
                            </Button>
                          </div>
                          <input
                            type="file"
                            ref={advisorFileInputRef}
                            onChange={(e) => handleImageUpload(e, 'advisor')}
                            className="hidden"
                            accept="image/*"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? 'Guardando...' : (editingTrip ? 'Actualizar' : 'Crear')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <MediaSelector 
        open={!!showMediaSelector} 
        onOpenChange={(open) => !open && setShowMediaSelector(null)} 
        onSelect={(url) => setFormData(prev => ({ 
          ...prev, 
          [showMediaSelector === 'advisor' ? 'advisor_image' : 'image_url']: url 
        }))} 
      />

      {/* Trips Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton h-16 rounded-lg" />
            ))}
          </div>
        ) : trips.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="trips-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 font-medium text-muted-foreground">Viaje</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Categoría</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Precio</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Estado</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((trip, index) => (
                  <tr key={trip.id} className="border-t" data-testid={`trip-row-${index}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={trip.image_url}
                          alt={trip.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <p className="font-medium text-primary">{trip.title}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-muted-foreground">{trip.destination}</p>
                            {trip.discount && (
                              <span className="text-[10px] bg-accent/20 text-accent px-1.5 py-0.5 rounded-full font-bold">
                                🏷️ {trip.discount}
                              </span>
                            )}
                            {trip.show_advisor && trip.advisor_name && (
                              <span className="text-[10px] bg-slate-100 text-slate-500 px-1 rounded flex items-center gap-1">
                                👨‍💼 {trip.advisor_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {CATEGORIES.find(c => c.value === trip.category)?.label || trip.category}
                    </td>
                    <td className="p-4 font-medium text-primary">
                      {trip.price.toLocaleString('es-ES')}€
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {trip.is_featured && (
                          <Star className="w-4 h-4 text-accent fill-accent" />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleTripStatus(trip)}
                          className={`text-xs px-2 py-1 h-auto rounded-full ${
                            trip.is_active 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {trip.is_active ? (
                            <><Eye className="w-3 h-3 mr-1" /> Activo</>
                          ) : (
                            <><EyeOff className="w-3 h-3 mr-1" /> Inactivo</>
                          )}
                        </Button>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(trip)}
                          data-testid={`edit-trip-${index}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(trip.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-trip-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No hay viajes creados. ¡Crea tu primera oferta!
          </div>
        )}
      </div>
      <MediaSelector
        open={showMediaSelector !== null}
        onOpenChange={(open) => !open && setShowMediaSelector(null)}
        onSelect={(url) => {
          if (showMediaSelector === 'trip') {
            const finalUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
            setFormData(prev => ({ ...prev, image_url: finalUrl }));
          } else if (showMediaSelector === 'advisor') {
            const finalUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
            setFormData(prev => ({ ...prev, advisor_image: finalUrl }));
          }
          setShowMediaSelector(null);
        }}
        type="image"
      />
    </AdminLayout>
  );
}
